import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle2,
  FolderPlus,
  KeyRound,
  Layers,
  UserPlus,
} from 'lucide-react';
import { LoadingIndicator } from '../../components/onboarding/LoadingIndicator';
import { useAuth } from '../../contexts/AuthContext';
import {
  canCreateProjects,
  canCreateUsers,
  canEditOrgUsers,
  formatWelcomeName,
  isPlatformAdminUser,
} from '../../types/onboarding';
import { pageRoutes } from '../../constants/routes';
import { Button } from '../../components/onboarding/Button';
import OnboardingSubHeader from '../../components/onboarding/OnboardingSubHeader';
import { ActiveOrganizationNotice } from '../../components/onboarding/ActiveOrganizationNotice';
import OrphanOrgAccessPanel from '../../components/onboarding/OrphanOrgAccessPanel';
import { OnboardingSurface } from '../../components/onboarding/Surface';
import { twMerge as cn } from 'tailwind-merge';

type SuccessType = 'project' | 'user' | 'user-edit' | 'user-membership-removed';

const SUCCESS_MESSAGES: Record<SuccessType, string> = {
  project: 'Project created successfully!',
  user: 'User created successfully! A welcome email has been sent with sign-in instructions.',
  'user-edit': 'User details updated successfully!',
  'user-membership-removed':
    'User removed from organization successfully.',
};

function OnboardingHome() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser, isLoading, isOrphanOrgUser, error } = useAuth();
  const [showSuccess, setShowSuccess] = useState(false);
  const [successType, setSuccessType] = useState<SuccessType | null>(null);
  const [removedOrgName, setRemovedOrgName] = useState<string | null>(null);

  const canProject = Boolean(
    currentUser && canCreateProjects(currentUser.platformRole)
  );
  const canUser = Boolean(
    currentUser && canCreateUsers(currentUser.platformRole)
  );
  // Edit User card visibility:
  //   - Admin / Maintainer get the full picker-driven "Edit User Details"
  //     flow (Admin: global; Maintainer: active org). Both are gated by
  //     `canEditOrgUsers`.
  //   - Developer can still self-edit their own last name + phone, so the
  //     card is always reachable; EditUser.tsx itself decides which mode
  //     to render based on the caller's role.
  const canEditOthers = Boolean(
    currentUser && canEditOrgUsers(currentUser.platformRole)
  );
  const canEnterEditUser = Boolean(currentUser);
  const isPlatformAdmin = isPlatformAdminUser(currentUser);
  const isOrgMaintainer =
    Boolean(currentUser) &&
    currentUser?.platformRole === 'maintainer' &&
    !isPlatformAdmin;
  const isOrgDeveloper =
    Boolean(currentUser) &&
    currentUser?.platformRole === 'developer' &&
    !isPlatformAdmin;

  const welcomeSubtitle = isOrphanOrgUser
    ? 'Your account needs an organization assignment before you can use the platform.'
    : isPlatformAdmin
    ? 'Get started by creating a new project or adding a new user to the platform.'
    : isOrgDeveloper
    ? 'View projects assigned to you and keep your profile up to date.'
    : 'Get started by creating a new project or inviting someone to your organization.';

  useEffect(() => {
    const success = searchParams.get('success');
    if (
      success === 'project' ||
      success === 'user' ||
      success === 'user-edit' ||
      success === 'user-membership-removed'
    ) {
      setSuccessType(success);
      setShowSuccess(true);
      setRemovedOrgName(
        success === 'user-membership-removed'
          ? searchParams.get('org')
          : null,
      );
      // Clear the query param without navigating away
      const next = new URLSearchParams(searchParams);
      next.delete('success');
      next.delete('org');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  if (isLoading && !currentUser) {
    return (
      <>
        <OnboardingSubHeader title="Onboarding" />
        <main className="flex flex-1 flex-col bg-surface-muted">
          <LoadingIndicator layout="page" />
        </main>
      </>
    );
  }

  return (
    <>
      <OnboardingSubHeader title="Onboarding" />

      <main className="bg-surface-muted px-6 py-10 sm:px-10 lg:py-14">
        <div className="mx-auto max-w-5xl">
          {error && (
            <div className="mb-6 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {showSuccess && successType && (
            <div className="onboarding-success-banner mb-8 flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <p className="text-green-800">
                  {successType === 'user-membership-removed' && removedOrgName
                    ? `User removed from ${removedOrgName} successfully.`
                    : SUCCESS_MESSAGES[successType]}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowSuccess(false)}
                className="text-green-600 hover:text-green-800"
              >
                Dismiss
              </button>
            </div>
          )}

          <div className="mb-8 space-y-2 text-center">
            <h1 className="text-3xl font-bold text-brand sm:text-4xl">
              Welcome,{' '}
              {formatWelcomeName(
                currentUser?.firstName,
                currentUser?.lastName
              )}
              !
            </h1>
            <p className="mx-auto max-w-2xl text-base text-gray-600">
              {welcomeSubtitle}
            </p>
            {!isOrphanOrgUser ? (
              <ActiveOrganizationNotice className="mx-auto max-w-2xl text-sm" />
            ) : null}
          </div>

          {isOrphanOrgUser ? (
            <OrphanOrgAccessPanel email={currentUser?.email} />
          ) : (
          <div className="grid gap-8 md:grid-cols-2 md:items-stretch">
            <OnboardingCard
              icon={
                <FolderPlus className="h-7 w-7 text-brand" aria-hidden />
              }
              title={isOrgDeveloper ? 'Your Projects' : 'New Project'}
              description={
                isOrgDeveloper
                  ? 'Open runs and work on projects you have been assigned to in your organization.'
                  : 'Create a new project for a customer and assign team members to work on it.'
              }
              bullets={
                isOrgDeveloper
                  ? [
                      'View projects in your active organization',
                      'Open runs for projects you are assigned to',
                      'Contact your Maintainer to request access to more projects',
                    ]
                  : [
                      'Enter project details and customer info',
                      'Set project timeline and milestones',
                      'Assign users to the project',
                    ]
              }
              primaryLabel={
                isOrgDeveloper ? 'View Projects' : 'Create New Project'
              }
              primaryClassName="h-11 w-full bg-brand text-white hover:bg-brand-hover"
              onPrimaryClick={() =>
                navigate(
                  isOrgDeveloper
                    ? pageRoutes.onboardingProjects
                    : pageRoutes.onboardingProject
                )
              }
              primaryDisabled={!isOrgDeveloper && !canProject}
              showRestriction={!isOrgDeveloper && !canProject}
              restrictionMessage={
                isOrgMaintainer
                  ? 'Switch to an organization where you are a Maintainer to create projects.'
                  : 'Only Admins and Maintainers can create projects.'
              }
              showSecondary={!isOrgDeveloper}
              secondaryLabel="View Projects"
              secondaryIcon={<Layers className="h-4 w-4 shrink-0" aria-hidden />}
              secondaryButtonClassName="h-11 w-full border-2 border-brand bg-white text-brand hover:bg-gray-50"
              onSecondaryClick={() =>
                navigate(pageRoutes.onboardingProjects)
              }
            />

            <OnboardingCard
              icon={
                <UserPlus className="h-7 w-7 text-accent" aria-hidden />
              }
              title={isOrgDeveloper ? 'Your Profile' : 'Create User'}
              description={
                isOrgDeveloper
                  ? 'Update your contact details and review your project assignments.'
                  : isPlatformAdmin
                  ? 'Create an Admin account or assign organization membership and role.'
                  : 'Invite someone to your organization and assign their role.'
              }
              bullets={
                isOrgDeveloper
                  ? [
                      'Update your name and phone number',
                      'View projects you are assigned to',
                      'Contact a Maintainer for role changes',
                    ]
                  : isPlatformAdmin
                  ? [
                      'Enter user details and contact info',
                      'Assign organization membership',
                      'Set Admin, Maintainer, or Developer role',
                    ]
                  : [
                      'Enter user details and contact info',
                      'Add them to your active organization',
                      'Set Maintainer or Developer role',
                    ]
              }
              primaryLabel={
                isOrgDeveloper
                  ? 'Edit Profile'
                  : 'Create New User'
              }
              primaryClassName={
                isOrgDeveloper
                  ? 'h-11 w-full bg-accent text-white hover:bg-accent-hover'
                  : 'h-11 w-full bg-accent text-white hover:bg-accent-hover'
              }
              onPrimaryClick={() =>
                navigate(
                  isOrgDeveloper
                    ? pageRoutes.onboardingEditUser
                    : pageRoutes.onboardingUser
                )
              }
              primaryDisabled={!isOrgDeveloper && !canUser}
              showRestriction={!isOrgDeveloper && !canUser}
              restrictionMessage={
                isOrgMaintainer
                  ? 'Switch to an organization where you are a Maintainer to invite users.'
                  : 'Only Admins and Maintainers can invite users.'
              }
              showSecondary={!isOrgDeveloper}
              secondaryLabel={
                canEditOthers ? 'Edit User Details' : 'Edit My Profile'
              }
              secondaryIcon={<KeyRound className="h-4 w-4 shrink-0" aria-hidden />}
              secondaryButtonClassName="h-11 w-full border-2 border-accent bg-white text-accent hover:bg-sky-50"
              onSecondaryClick={() =>
                navigate(pageRoutes.onboardingEditUser)
              }
              secondaryDisabled={!canEnterEditUser}
            />
          </div>
          )}
        </div>
      </main>
    </>
  );
}

interface OnboardingCardProps {
  icon: JSX.Element;
  title: string;
  description: string;
  bullets: string[];
  primaryLabel: string;
  primaryClassName: string;
  onPrimaryClick: () => void;
  primaryDisabled: boolean;
  showRestriction?: boolean;
  restrictionMessage?: string;
  secondaryLabel: string;
  secondaryIcon: JSX.Element;
  secondaryButtonClassName: string;
  onSecondaryClick: () => void;
  secondaryDisabled?: boolean;
  showSecondary?: boolean;
}

function OnboardingCard({
  icon,
  title,
  description,
  bullets,
  primaryLabel,
  primaryClassName,
  onPrimaryClick,
  primaryDisabled,
  showRestriction,
  restrictionMessage,
  secondaryLabel,
  secondaryIcon,
  secondaryButtonClassName,
  onSecondaryClick,
  secondaryDisabled = false,
  showSecondary = true,
}: OnboardingCardProps) {
  return (
    <OnboardingSurface className="flex flex-col overflow-hidden shadow-md">
      <div className="flex flex-1 flex-col p-6 sm:p-8">
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-lg bg-sky-100">
          {icon}
        </div>
        <h3 className="mb-2 text-xl font-bold text-brand">{title}</h3>
        <p className="mb-5 text-sm leading-relaxed text-gray-600">
          {description}
        </p>

        <div className="mb-6 space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            What you can do:
          </p>
          <ul className="space-y-2 text-sm text-gray-600">
            {bullets.map((bullet) => (
              <li key={bullet} className="flex items-start gap-2">
                <span
                  className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent"
                  aria-hidden
                />
                {bullet}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-auto flex flex-col gap-3">
          {showRestriction && restrictionMessage && (
            <div className="onboarding-restriction-callout p-3">
              <p className="text-sm text-yellow-800">
                <span className="font-medium">Access restricted.</span>{' '}
                {restrictionMessage}
              </p>
            </div>
          )}

          <Button
            onClick={onPrimaryClick}
            disabled={primaryDisabled}
            variant="default"
            className={cn(
              primaryClassName,
              primaryDisabled &&
                'cursor-not-allowed bg-gray-100 text-gray-400 hover:bg-gray-100'
            )}
          >
            {primaryLabel}
          </Button>

          {showSecondary && (
          <Button
            type="button"
            variant="outline"
            disabled={secondaryDisabled}
            onClick={onSecondaryClick}
            className={cn(
              secondaryButtonClassName,
              secondaryDisabled &&
                'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
            )}
          >
            {secondaryIcon}
            {secondaryLabel}
          </Button>
          )}
        </div>
      </div>
    </OnboardingSurface>
  );
}

export default OnboardingHome;
